import { Injectable, inject } from '@angular/core';
import moment, { Moment } from 'moment';
import { TransitDataService } from '../transit-data.service';
import { clampHdRaveNewYear, findHdRaveNewYearMoment, hdRaveNewYearSearchWindow } from './hd-rave-new-year';

/** Welke Rave New Year-chart: volledig (design + personality) of enkel de transit (personality). */
export type HdRaveNewYearVariant = 'full' | 'transit';

/** Aanvraag om de Rave New Year-chart van een jaar te openen. */
export interface HdRaveNewYearRequest {
  readonly year: number;
  readonly variant: HdRaveNewYearVariant;
}

/**
 * Het moment van Rave New Year per jaar (de zon gaat poort 41 binnen), uit de
 * zon-lijnsegmenten van de transits. Via {@link TransitDataService}, zodat het
 * de chunk-cache van de transits deelt; per jaar gecachet (de data is
 * deterministisch). Een mislukte lading blijft niet in de cache.
 */
@Injectable({ providedIn: 'root' })
export class HdRaveNewYearService {
  private readonly dataService = inject(TransitDataService);
  private readonly moments = new Map<number, Promise<Moment | null>>();

  /** Tijdstip (UTC) van Rave New Year in `year`, of `null` als het niet te bepalen was. */
  momentFor(year: number): Promise<Moment | null> {
    const key = clampHdRaveNewYear(year);
    const cached = this.moments.get(key);
    if (cached != null) return cached;
    const pending = this.load(key).then(found => {
      if (found == null) this.moments.delete(key);
      return found;
    });
    this.moments.set(key, pending);
    return pending;
  }

  /**
   * Het Rave-jaar waarin `now` valt: vóór de intrede van dit kalenderjaar is
   * dat nog het vorige jaar. Kan het moment niet bepaald worden, dan het
   * kalenderjaar.
   */
  async currentYear(now: Moment = moment()): Promise<number> {
    const year = moment.utc(now).year();
    const start = await this.momentFor(year);
    return start != null && now.isBefore(start) ? year - 1 : year;
  }

  private async load(year: number): Promise<Moment | null> {
    const { fromUtc, toUtc } = hdRaveNewYearSearchWindow(year);
    const [sun] = await this.dataService.getSegments(fromUtc, toUtc, ['sun'], 'line');
    return sun == null ? null : findHdRaveNewYearMoment(sun.segments);
  }
}
